const path = require('path')

const EXTERNAL_PATH = /^(https?:|mailto:|tel:)/

const asArray = value => (Array.isArray(value) ? value : [])

export const TENANT_SIDEBAR_DEFAULT_WIDTH = 220
export const TENANT_SIDEBAR_MIN_WIDTH = 180
export const TENANT_SIDEBAR_MAX_WIDTH = 420
export const TENANT_SIDEBAR_WIDTH_STORAGE_KEY = 'tenant-sidebar-width'

export const normalizeTenantSidebarWidth = value => {
  if (value === null || value === undefined || value === '') {
    return TENANT_SIDEBAR_DEFAULT_WIDTH
  }
  const width = Number(value)
  if (!Number.isFinite(width)) {
    return TENANT_SIDEBAR_DEFAULT_WIDTH
  }
  return Math.min(
    TENANT_SIDEBAR_MAX_WIDTH,
    Math.max(TENANT_SIDEBAR_MIN_WIDTH, Math.round(width))
  )
}

export const readTenantSidebarWidth = storage => {
  try {
    return normalizeTenantSidebarWidth(
      storage && storage.getItem(TENANT_SIDEBAR_WIDTH_STORAGE_KEY)
    )
  } catch (error) {
    return TENANT_SIDEBAR_DEFAULT_WIDTH
  }
}

export const saveTenantSidebarWidth = (storage, value) => {
  const width = normalizeTenantSidebarWidth(value)
  try {
    if (storage) {
      storage.setItem(TENANT_SIDEBAR_WIDTH_STORAGE_KEY, String(width))
    }
  } catch (error) {
    // Storage can be unavailable in privacy mode; resizing still works in-memory.
  }
  return width
}

const defaultResolvePath = (basePath, routePath) => {
  const nextPath = String(routePath || '')
  if (EXTERNAL_PATH.test(nextPath)) {
    return nextPath
  }
  if (EXTERNAL_PATH.test(String(basePath || ''))) {
    return String(basePath)
  }
  return path.resolve(String(basePath || '/'), nextPath)
}

export const buildTenantRouteSidebarNodes = (routes, options = {}) => {
  const isVisible = options.isVisible || (route => !route.hidden)
  const hasAccess = options.hasAccess || (() => true)
  const getChildren = options.getChildren || (route => route.children)
  const getTitle = options.getTitle || (meta => meta.title || '')
  const getIcon = options.getIcon || (meta => meta.icon || '')
  const getLeafTarget = options.getLeafTarget || ((route, currentPath) => currentPath)
  const getNodePath = options.getNodePath || ((route, currentPath) => currentPath)
  const resolvePath = options.resolvePath || defaultResolvePath

  const normalizeRoute = (route, currentPath) => {
    if (!route || !isVisible(route) || !hasAccess(route)) {
      return null
    }
    const nodePath = getNodePath(route, currentPath)

    const visibleChildren = asArray(getChildren(route))
      .filter(child => child && isVisible(child) && hasAccess(child))
    const flattenSingleChild = options.flattenSingleChild !== false
      && !route.alwaysShow
      && visibleChildren.length <= 1

    if (flattenSingleChild) {
      const child = visibleChildren[0]
      const leaf = child || route
      const targetPath = child
        ? resolvePath(nodePath, child.path)
        : getLeafTarget(route, nodePath)
      const meta = {
        ...(route.meta || {}),
        ...((child && child.meta) || {})
      }
      return {
        key: targetPath,
        index: targetPath,
        target: targetPath,
        external: EXTERNAL_PATH.test(String(targetPath)),
        title: getTitle(meta, leaf, route),
        icon: getIcon(meta, leaf, route),
        children: [],
        source: leaf
      }
    }

    const children = visibleChildren
      .map(child => normalizeRoute(
        child,
        resolvePath(nodePath, child.path)
      ))
      .filter(Boolean)

    return {
      key: nodePath,
      index: nodePath,
      target: nodePath,
      external: EXTERNAL_PATH.test(String(nodePath)),
      title: getTitle(route.meta || {}, route, route),
      icon: getIcon(route.meta || {}, route, route),
      children,
      source: route
    }
  }

  return asArray(routes)
    .map(route => normalizeRoute(
      route,
      defaultResolvePath('/', route && route.path)
    ))
    .filter(Boolean)
}
